import type { MagicTake } from '../shared/magic-effect-contract';
import { reserveEffectDevice } from './roll-audio-mixer';

let context: AudioContext | null = null;
const buffers = new Map<string, Promise<AudioBuffer>>();
const playing = new Map<string, () => void>();
export const magicAudioReceipt: Array<Record<string, unknown>> = [];
const report = (row: Record<string, unknown>) => { magicAudioReceipt.push(row); if (magicAudioReceipt.length > 200) magicAudioReceipt.shift(); };
export function magicAudioState(): string { return context?.state ?? 'uninitialized'; }
function device(): AudioContext { return context ??= new AudioContext(); }
if (window.parent === window && new URLSearchParams(location.search).get('magicMissilePilot') === '1')
  for (const event of ['pointerdown','keydown']) document.addEventListener(event, () => { void device().resume().catch(() => {}); }, { passive: true });
export function prepareMagicAudio(take: MagicTake): Promise<AudioBuffer> {
  let pending = buffers.get(take.revision);
  if (!pending) {
    pending = (async () => {
      const response = await fetch(take.url, { signal: AbortSignal.timeout(10000) });
      if (!response.ok) throw new Error('Effect audio unavailable');
      const bytes = await response.arrayBuffer();
      if (bytes.byteLength > 4 * 1024 * 1024) throw new Error('Effect audio exceeds budget');
      const sha = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), b => b.toString(16).padStart(2,'0')).join('');
      if (sha !== take.sha256) throw new Error('Effect audio revision mismatch');
      return device().decodeAudioData(bytes);
    })();
    buffers.set(take.revision, pending);
    pending.catch(() => buffers.delete(take.revision));
  }
  return pending;
}
export function cancelMagicAudio(id?: string): void {
  if (id) playing.get(id)?.(); else for (const stop of [...playing.values()]) stop();
}
/** Dedicated event/phase API. Parent device controls and channel reservations remain authoritative. */
export async function scheduleMagicAudio(id: string, take: MagicTake, impactAt: number, eligible: () => boolean): Promise<void> {
  if (playing.has(id)) return;
  let cleanup: (() => void) | undefined;
  try {
    const buffer = await prepareMagicAudio(take), ctx = device();
    const late = performance.now() - impactAt;
    if (!eligible() || document.hidden || late > 75 || ctx.state !== 'running') {
      report({ id, status: 'skipped', reason: !eligible() ? 'scene-ineligible' : document.hidden ? 'hidden' : late > 75 ? 'late' : 'autoplay', late }); return;
    }
    const source = ctx.createBufferSource(), gain = ctx.createGain();
    let release: (() => void) | undefined;
    let removeControl: (() => void) | undefined;
    const stop = () => { try { source.stop(); } catch {} source.disconnect(); gain.disconnect(); release?.(); removeControl?.(); playing.delete(id); };
    cleanup = stop;
    const slot = reserveEffectDevice(stop);
    if (!slot) { report({ id, status: 'skipped', reason: 'muted-or-no-device' }); return; }
    release = slot.release;
    const controls = () => { gain.gain.value = slot.player.muted ? 0 : slot.player.volume; };
    slot.player.addEventListener('volumechange', controls); controls();
    removeControl = () => slot.player.removeEventListener('volumechange', controls);
    source.buffer = buffer; source.connect(gain); gain.connect(ctx.destination);
    const output = ctx.getOutputTimestamp?.() ?? {};
    const outputClockReady = output.contextTime !== undefined && output.contextTime > 0 && output.performanceTime !== undefined && output.performanceTime > 0;
    const desiredStart = outputClockReady ? output.contextTime! + (impactAt - output.performanceTime!) / 1000
      : ctx.currentTime + (impactAt - performance.now()) / 1000;
    const startAt = Math.max(ctx.currentTime, desiredStart);
    const estimatedOutputAt = outputClockReady ? output.performanceTime! + (startAt - output.contextTime!) * 1000 : impactAt;
    if (estimatedOutputAt - impactAt > 75) { stop(); report({ id, status: 'skipped', reason: 'late-output', late: estimatedOutputAt - impactAt }); return; }
    playing.set(id, stop); source.onended = () => { release?.(); removeControl?.(); playing.delete(id); source.disconnect(); gain.disconnect(); };
    source.start(startAt);
    report({ id, status: 'scheduled', audioId: take.audioId, revision: take.revision, impactAt,
      audioContextStart: startAt, estimatedOutputAt, outputClockReady, baseLatencyMs: ctx.baseLatency * 1000,
      scheduledAt: performance.now(), evicted: slot.evicted, channel: slot.player.id });
  } catch (error) { cleanup?.(); report({ id, status: 'skipped', reason: String(error) }); }
}
